import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { scanApi }   from '../api/httpClient';
import { userApi }   from '../api/userApi';
import { useAuth }   from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';

/** V2 VolunteerScanner — dark field tool, CSS animations, no framer-motion */

const RESULT_DURATION = 3500;

const ZONE_TYPE_CFG = {
  entry:   { icon: '🚪', color: '#C81E78' },
  exit:    { icon: '🚶', color: '#8E8597' },
  food:    { icon: '🍽',  color: '#FF9800' },
  session: { icon: '🎤', color: '#00D4AA' },
  sponsor: { icon: '🎯', color: '#EC4899' },
  general: { icon: '📍', color: '#8E8597' },
};

// ── CSS keyframes injected once ───────────────────────────────────────────────
const STYLES = `
  @keyframes vsRotate { to { transform: rotate(360deg); } }
  @keyframes vsPulse  { 0%,100% { box-shadow: 0 0 0 0 rgba(0,200,83,0.5); } 70% { box-shadow: 0 0 0 28px rgba(0,200,83,0); } }
  @keyframes vsDenyPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(255,61,87,0.5); } 70% { box-shadow: 0 0 0 28px rgba(255,61,87,0); } }
  @keyframes vsShake  { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-10px)} 40%{transform:translateX(10px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(6px)} }
  @keyframes vsPopIn  { from { opacity:0; transform:scale(0.7); } to { opacity:1; transform:scale(1); } }
  @keyframes vsFadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes vsScanLine { 0%,100% { top: 10%; } 50% { top: 85%; } }
  @keyframes vsBlink  { 0%,100% { opacity:1; } 50% { opacity:0.35; } }
  @keyframes vsFlash  { from { opacity:0.35; } to { opacity:0; } }
`;

// ── Shared card-click row style ───────────────────────────────────────────────
const clickCard = (color, active) => ({
  background: 'rgba(255,255,255,0.06)',
  border: `1px solid ${active ? color + '80' : 'rgba(255,255,255,0.12)'}`,
  borderLeft: `4px solid ${color}`,
  borderRadius: 14, padding: '16px 20px', cursor: 'pointer',
  transition: 'box-shadow 0.2s', marginBottom: 10,
  display: 'flex', alignItems: 'center', gap: 14,
});

// ── Live countdown to a target datetime ───────────────────────────────────────
function Countdown({ to, prefix = '' }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(to).getTime() - now;
  if (isNaN(ms)) return null;
  if (ms <= 0) return <span>{prefix}starting now…</span>;
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const sc = Math.floor((ms % 60000) / 1000);
  const str = d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${sc}s` : `${m}m ${sc}s`;
  return <span>{prefix}{str}</span>;
}

// ── Sign-out button (fixed, top-right) ────────────────────────────────────────
function SignOutBtn({ onLogout }) {
  return (
    <button onClick={onLogout} title="Sign out"
      style={{ position: 'fixed', top: 14, right: 14, zIndex: 50,
        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
        color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 6 }}>
      ⏻ Sign out
    </button>
  );
}

// ── Event selector screen ─────────────────────────────────────────────────────
function EventSelector({ events, loading, onSelect, onLogout }) {
  return (
    <div style={{ minHeight: '100vh', background: '#0F0E1A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      {onLogout && <SignOutBtn onLogout={onLogout} />}
      <div style={{ textAlign: 'center', marginBottom: 32, animation: 'vsFadeUp 0.4s ease' }}>
        <div style={{ width: 56, height: 56, background: 'linear-gradient(115deg,#7B2FE4,#C71D8E,#F5176B)', borderRadius: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, marginBottom: 14 }}>⚡</div>
        <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 6px' }}>Select Event</h2>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, margin: 0 }}>Choose the event you are working at today</p>
      </div>

      <div style={{ width: '100%', maxWidth: 380 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,0.15)', borderTopColor: '#C81E78', borderRadius: '50%', animation: 'vsRotate 1s linear infinite' }} />
          </div>
        ) : events.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📅</div>
            <p>No events assigned to you.<br />Contact your event admin.</p>
          </div>
        ) : events.map(event => {
          const isLive = event.status === 'live';
          return (
            <div key={event.id} onClick={() => onSelect(event)} style={clickCard(isLive ? '#00C853' : '#C81E78', isLive)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{event.name}</div>
                {event.location && <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginBottom: 4 }}>📍 {event.location}</div>}
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
                  {new Date(event.start_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                {!isLive && event.start_at && new Date(event.start_at).getTime() > Date.now() && (
                  <div style={{ color: '#F5A623', fontSize: 12, fontWeight: 600, marginTop: 4 }}>
                    ⏳ Scanning opens in <Countdown to={event.start_at} />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginLeft: 12 }}>
                {isLive ? (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#00C853', display: 'flex', alignItems: 'center', gap: 4, animation: 'vsBlink 1.5s infinite' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#00C853', display: 'inline-block' }} /> LIVE
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontWeight: 600, textTransform: 'uppercase' }}>{event.status}</span>
                )}
                <span style={{ color: '#C81E78', fontSize: 20 }}>›</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Zone selector screen ──────────────────────────────────────────────────────
function ZoneSelector({ zones, eventName, onSelect, onBack, onLogout }) {
  return (
    <div style={{ minHeight: '100vh', background: '#0F0E1A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      {onLogout && <SignOutBtn onLogout={onLogout} />}
      <div style={{ textAlign: 'center', marginBottom: 32, animation: 'vsFadeUp 0.4s ease' }}>
        <div style={{ width: 56, height: 56, background: 'linear-gradient(115deg,#7B2FE4,#C71D8E,#F5176B)', borderRadius: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, marginBottom: 14 }}>⚡</div>
        <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Select Your Zone</h2>
        {eventName && <p style={{ color: '#C81E78', fontSize: 13, fontWeight: 600, margin: '0 0 4px' }}>{eventName}</p>}
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, margin: 0 }}>Choose the zone you are stationed at</p>
      </div>

      <div style={{ width: '100%', maxWidth: 380 }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 16px', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 600, marginBottom: 16, display: 'block', width: '100%' }}>
            ← Change Event
          </button>
        )}
        {zones.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: 32 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
            <p>You haven't been assigned to any zones for this event.<br />Contact your event admin.</p>
          </div>
        ) : zones.map(zone => {
          const cfg = ZONE_TYPE_CFG[zone.type] || ZONE_TYPE_CFG.general;
          return (
            <div key={zone.id} onClick={() => onSelect(zone)} style={clickCard(cfg.color, false)}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: cfg.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{cfg.icon}</div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{zone.name}</div>
                <div style={{ color: cfg.color, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', marginTop: 2 }}>{zone.type} zone</div>
              </div>
              <div style={{ marginLeft: 'auto', color: cfg.color, fontSize: 20 }}>›</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main scanner ──────────────────────────────────────────────────────────────
export default function VolunteerScanner({ zoneId: propZoneId, zoneName: propZoneName, eventId: propEventId }) {
  const { user, logout } = useAuth();
  const params      = useParams();
  const navigate    = useNavigate();
  const handleLogout = async () => { try { await logout(); } catch {} navigate('/login'); };

  const urlEventId      = params.eventId ? Number(params.eventId) : null;
  const resolvedEventId = urlEventId || propEventId || null;

  const [step, setStep]               = useState(resolvedEventId ? 'zone' : 'event');
  const [events, setEvents]           = useState([]);
  const [eventsLoading, setEventsLoading] = useState(!resolvedEventId);
  const [selectedEvent, setSelectedEvent] = useState(resolvedEventId ? { id: resolvedEventId, name: '' } : null);
  const [availableZones, setZones]    = useState([]);
  const [selectedZone, setSelectedZone] = useState(propZoneId ? { id: propZoneId, name: propZoneName || 'Zone', type: 'entry' } : null);
  const [scanResult, setScanResult]   = useState(null);
  const [scanning, setScanning]       = useState(false);
  const [error, setError]             = useState('');
  const [processing, setProcessing]   = useState(false);
  const scannerRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!resolvedEventId && user) {
      setEventsLoading(true);
      userApi.myEvents()
        .then(d => {
          const evs = d.events || [];
          setEvents(evs);
          if (evs.length === 1) { setSelectedEvent(evs[0]); setStep('zone'); }
        })
        .catch(() => setEvents([]))
        .finally(() => setEventsLoading(false));
    }
  }, [resolvedEventId, user]);

  useEffect(() => {
    if (!selectedEvent || propZoneId) return;
    userApi.myZones(selectedEvent.id)
      .then(d => {
        const zones = d.zones || [];
        setZones(zones);
        if (zones.length === 1) { setSelectedZone(zones[0]); setStep('scan'); }
      })
      .catch(() => setZones([]));
  }, [selectedEvent, propZoneId]);

  const handleSelectEvent = (event) => { setSelectedEvent(event); setSelectedZone(null); setZones([]); setStep('zone'); };
  const handleSelectZone  = (zone)  => { setSelectedZone(zone); setStep('scan'); };

  const handleBackToZones = () => { stopScanner(); setScanResult(null); setSelectedZone(null); setStep('zone'); };
  const handleBackToEvents = () => { stopScanner(); setScanResult(null); setSelectedZone(null); setSelectedEvent(null); setZones([]); setStep('event'); };

  const startScanner = async () => {
    setError(''); setScanResult(null); setScanning(true);
    const scanner = new Html5Qrcode('qr-reader');
    scannerRef.current = scanner;
    try {
      await scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 240, height: 240 } }, handleQrCode, () => {});
    } catch {
      setError('Camera access denied. Please allow camera permissions.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) { try { await scannerRef.current.stop(); } catch {} scannerRef.current = null; }
    setScanning(false);
  };

  const handleQrCode = async (decodedText) => {
    if (processing || !selectedZone) return;
    setProcessing(true);
    await stopScanner();
    try {
      const data = await scanApi.scan(decodedText, selectedZone.id);
      setScanResult(data);
    } catch (err) {
      setScanResult({ result: 'DENY', reason: err.message || 'SCAN_ERROR' });
    }
    timeoutRef.current = setTimeout(() => { setScanResult(null); setProcessing(false); startScanner(); }, RESULT_DURATION);
  };

  useEffect(() => () => { stopScanner(); clearTimeout(timeoutRef.current); }, []);

  if (step === 'event') return <EventSelector events={events} loading={eventsLoading} onSelect={handleSelectEvent} onLogout={handleLogout} />;
  if (step === 'zone' || !selectedZone) return <ZoneSelector zones={availableZones} eventName={selectedEvent?.name} onSelect={handleSelectZone} onBack={!resolvedEventId ? handleBackToEvents : null} onLogout={handleLogout} />;

  const isAllow = scanResult?.result === 'ALLOW';
  const isDeny  = scanResult?.result === 'DENY';
  // Gate scanning until the event is live. Status unknown (deep-link) → allow.
  const eventLive = selectedEvent?.status ? selectedEvent.status === 'live' : true;
  const zoneCfg = ZONE_TYPE_CFG[selectedZone.type] || ZONE_TYPE_CFG.general;
  const boxW    = Math.min(280, (typeof window !== 'undefined' ? window.innerWidth : 320) - 64);

  return (
    <div style={{ minHeight: '100vh', background: '#0F0E1A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <style>{STYLES}</style>

      {/* Zone header */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>{zoneCfg.icon}</span>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{selectedZone.name}</div>
            <div style={{ color: zoneCfg.color, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{selectedZone.type} zone</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!propZoneId && (
            <button onClick={handleBackToZones} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600 }}>
              ⇄ Change Zone
            </button>
          )}
          <button onClick={handleLogout} title="Sign out" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600 }}>
            ⏻ Sign out
          </button>
        </div>
      </div>

      {/* Gated: event not live yet */}
      {!scanResult && !eventLive && (
        <div style={{ width: '100%', maxWidth: 340, textAlign: 'center', animation: 'vsPopIn 0.3s ease' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>⏳</div>
          <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>Scanning not open yet</h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, margin: '0 0 4px' }}>
            Check-in unlocks when this event goes live.
          </p>
          {selectedEvent?.start_at && new Date(selectedEvent.start_at).getTime() > Date.now() && (
            <div style={{ color: '#F5A623', fontSize: 16, fontWeight: 700, marginTop: 12 }}>
              <Countdown to={selectedEvent.start_at} prefix="Starts in " />
            </div>
          )}
        </div>
      )}

      {/* Scanner */}
      {!scanResult && eventLive && (
        <div style={{ width: '100%', maxWidth: 320, textAlign: 'center', animation: 'vsPopIn 0.3s ease' }}>
          <div style={{ position: 'relative', width: boxW, height: boxW, margin: '0 auto 24px', borderRadius: 20, overflow: 'hidden', border: `2px solid ${zoneCfg.color}60` }}>
            <div id="qr-reader" style={{ width: '100%', height: '100%' }} />
            {scanning && (
              <div style={{ position: 'absolute', left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${zoneCfg.color}, transparent)`, boxShadow: `0 0 12px ${zoneCfg.color}`, animation: 'vsScanLine 2.5s ease-in-out infinite' }} />
            )}
            {['top-left','top-right','bottom-left','bottom-right'].map(corner => (
              <div key={corner} style={{
                position: 'absolute', width: 24, height: 24,
                [corner.includes('top') ? 'top' : 'bottom']: 12,
                [corner.includes('left') ? 'left' : 'right']: 12,
                borderTop:    corner.includes('top')    ? `3px solid ${zoneCfg.color}` : 'none',
                borderBottom: corner.includes('bottom') ? `3px solid ${zoneCfg.color}` : 'none',
                borderLeft:   corner.includes('left')   ? `3px solid ${zoneCfg.color}` : 'none',
                borderRight:  corner.includes('right')  ? `3px solid ${zoneCfg.color}` : 'none',
                borderRadius: corner === 'top-left' ? '4px 0 0 0' : corner === 'top-right' ? '0 4px 0 0' : corner === 'bottom-left' ? '0 0 0 4px' : '0 0 4px 0',
              }} />
            ))}
          </div>

          {!scanning ? (
            <button onClick={startScanner} style={{ background: `linear-gradient(135deg,${zoneCfg.color},${zoneCfg.color}CC)`, color: '#fff', border: 'none', borderRadius: 50, padding: '14px 40px', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
              📷 Start Scanning
            </button>
          ) : (
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>Point camera at attendee QR code</p>
          )}
          {error && <p style={{ color: '#FF3D57', fontSize: 13, marginTop: 10 }}>{error}</p>}
        </div>
      )}

      {/* ALLOW */}
      {isAllow && (
        <div style={{ textAlign: 'center', animation: 'vsPopIn 0.35s cubic-bezier(0.34,1.56,0.64,1)' }}>
          <div style={{ width: 120, height: 120, borderRadius: '50%', background: 'linear-gradient(135deg,#00C853,#69F0AE)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56, margin: '0 auto 24px', animation: 'vsPulse 1s 2' }}>✓</div>
          <h2 style={{ color: '#00C853', fontSize: 36, fontWeight: 800, marginBottom: 8, animation: 'vsFadeUp 0.3s 0.15s both ease' }}>ALLOWED</h2>
          <p style={{ color: '#fff', fontSize: 22, fontWeight: 600, marginBottom: 4, animation: 'vsFadeUp 0.3s 0.22s both ease' }}>{scanResult.attendee_display_name}</p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 15, animation: 'vsFadeUp 0.3s 0.28s both ease' }}>{scanResult.ticket_label}</p>
          {scanResult.remaining_entitlements !== undefined && (
            <div style={{ display: 'inline-block', background: 'rgba(0,200,83,0.15)', border: '1px solid rgba(0,200,83,0.3)', borderRadius: 50, padding: '4px 16px', color: '#69F0AE', fontSize: 14, marginTop: 8, animation: 'vsPopIn 0.3s 0.35s both ease' }}>
              {scanResult.remaining_entitlements} uses remaining
            </div>
          )}
        </div>
      )}

      {/* DENY */}
      {isDeny && (
        <div style={{ textAlign: 'center', animation: 'vsPopIn 0.25s ease, vsShake 0.5s 0.2s ease' }}>
          <div style={{ width: 120, height: 120, borderRadius: '50%', background: 'linear-gradient(135deg,#FF3D57,#FF6B8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56, margin: '0 auto 24px', animation: 'vsDenyPulse 0.8s 2' }}>✗</div>
          <h2 style={{ color: '#FF3D57', fontSize: 36, fontWeight: 800, marginBottom: 8 }}>DENIED</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16 }}>{reasonLabel(scanResult.reason)}</p>
        </div>
      )}

      {/* Background flash */}
      {scanResult && (
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: -1, background: isAllow ? '#00C853' : '#FF3D57', animation: 'vsFlash 0.6s ease forwards' }} />
      )}
    </div>
  );
}

function reasonLabel(reason) {
  const map = {
    NO_ACCESS:            'This ticket has no access to this zone',
    ALREADY_USED:         'Entitlement already used',
    EXPIRED:              'QR token has expired',
    ZONE_NOT_PERMITTED:   'Zone not permitted for this ticket',
    CONSENT_DENIED:       'Attendee has not consented',
    INVALID_TOKEN:        'Invalid or unrecognised QR code',
    EVENT_MISMATCH:       'QR code is for a different event',
    REGISTRATION_INVALID: 'Registration is not confirmed',
    OUTSIDE_TIME_WINDOW:  'Outside permitted time window',
    SCAN_ERROR:           'Scan failed — please try again',
  };
  return map[reason] || reason;
}
